<template>
  <div class="height-100">
    <tab-layout
      :class="{
        b2b: true,
      }"
      :disabled="isTrialExpired"
    >
      <template #banner>
        <ez-alert v-if="banner" customClass="venue-banner">
          <component :is="'style'">{{ css }}</component>
          <template #icon><font-awesome-icon icon="exclamation-circle" /></template>
          <div class="venue-banner__message">{{ banner.text }}</div>
          <template #cta v-if="banner.cta">
            <ez-button type="link" @click="onBannerCTAClick">
              {{ banner.cta }}
            </ez-button>
            <router-link
              v-if="isTrialExpired"
              :to="{ name: 'manage-accounts' }"
              class="banner__link ml-8"
            >
              Switch to a different account
            </router-link>
            <ez-button
              v-if="isTrialExpired"
              :data-cy="commonCy.ACCOUNT_SETTINGS.BUTTON__SIGN_OUT"
              type="link"
              formType="button"
              class="banner__link sign-out"
              @click="onLogoutClick"
            >
              Sign Out
              <font-awesome-icon icon="sign-out-alt" />
            </ez-button>
          </template>
        </ez-alert>
      </template>

      <template #nav>
        <ul class="nav">
          <li v-if="$permission.has('seeOverview') && !$permission.isFree && isLoggedIn">
            <router-link
              :data-cy="outletCy.MAIN_NAVIGATION.LINK__PRODUCTS"
              :to="{ name: 'venue-home' }"
            >
              Overview
            </router-link>
          </li>
          <li v-if="!groupViewMode && !isFreemium && isLoggedIn">
            <div class="sub-menu">
              <router-link
                :data-cy="outletCy.MAIN_NAVIGATION.LINK__ORDERS"
                :to="{ name: 'venue-orders' }"
              >
                Orders
              </router-link>
            </div>
            <ul class="sub-menu__dropdown">
              <li>
                <router-link
                  :to="{ name: 'venue-orders-history' }"
                  :data-cy="outletCy.ORDERS.GENERAL.LINK__ORDER_HISTORY"
                >
                  History
                </router-link>
                <ez-badge type="purple" :count="context.actionsCount || 0" />
              </li>
              <li v-if="$permission.has('createOrder') && $permission.has('seeProducts')">
                <router-link :to="{ name: 'venue-orders-products' }"> Ordering </router-link>
              </li>
              <li v-if="$permission.has('seeFutureOrders')">
                <router-link
                  :to="{ name: 'venue-orders-standing' }"
                  :data-cy="outletCy.ORDERS.GENERAL.LINK__STANDING_ORDERS"
                >
                  Automated Orders
                </router-link>
              </li>
            </ul>
          </li>
          <template v-else-if="!groupViewMode && (isFreemium || !isLoggedIn)">
            <li v-if="$permission.has('createOrder') && $permission.has('seeProducts')">
              <router-link :to="{ name: 'venue-orders-products' }"> All Products </router-link>
            </li>
            <li v-if="isLoggedIn">
              <router-link
                :to="{ name: 'venue-orders-history' }"
                :data-cy="outletCy.ORDERS.GENERAL.LINK__ORDER_HISTORY"
              >
                Orders
              </router-link>
              <ez-badge type="purple" :count="context.actionsCount || 0" />
            </li>
          </template>
          <li v-if="$permission.has('seePendingInvoices') && !$permission.isFree && isLoggedIn">
            <router-link
              :to="{ name: 'venue-pending-updates' }"
              :data-cy="outletCy.ORDERS.GENERAL.LINK__PENDING_UPDATES"
            >
              Invoices
            </router-link>
            <ez-badge type="error-red" :count="pendingInvoicesCount || 0" />
          </li>
          <li class="hide-on-mobile" v-if="$permission.has('seeSuppliers') && isLoggedIn">
            <template v-if="$permission.isLite || $permission.isPremium">
              <div class="sub-menu">
                <router-link
                  :data-cy="outletCy.MAIN_NAVIGATION.LINK__SUPPLIERS"
                  :to="{ name: 'venue-suppliers' }"
                >
                  Catalog
                </router-link>
                <ez-badge :count="supplierNotificationNumber" type="yellow" />
              </div>
              <ul class="sub-menu__dropdown">
                <li>
                  <router-link :to="{ name: 'venue-all-suppliers-products' }">
                    Products
                  </router-link>
                </li>
                <li>
                  <router-link :to="{ name: 'venue-all-suppliers' }">
                    {{ $t('global.distributors') }}
                  </router-link>
                </li>
                <li v-if="canSeeConnectionRequest">
                  <router-link :to="{ name: 'venue-connection-requests' }">
                    {{ $t('distributors.connectionRequestsTab') }}
                  </router-link>
                  <EzBadge :count="context.invitationsCount || 0" type="yellow" />
                </li>
              </ul>
            </template>
          </li>
          <li
            class="hide-on-mobile"
            v-if="isInventoryEnabled && hasManageInventoryAndConductStocktake && isLoggedIn"
          >
            <div class="sub-menu">
              <router-link :to="{ name: 'venue-stock' }">Inventory</router-link>
            </div>
            <ul class="sub-menu__dropdown">
              <li>
                <router-link
                  v-if="$permission.has('manageInventory')"
                  :to="{ name: 'venue-stock-all' }"
                >
                  Items
                </router-link>
              </li>
              <li :class="{ 'ml-0': !$permission.has('manageInventory') }">
                <router-link
                  v-if="$permission.has('conductStocktake')"
                  :to="{ name: 'venue-stock-stocktake' }"
                >
                  Counts
                </router-link>
              </li>
            </ul>
          </li>
          <li
            class="hide-on-mobile"
            v-else-if="isInventoryEnabled && hasManageInventory && isLoggedIn"
          >
            <router-link :to="{ name: 'venue-stock-all' }">Inventory</router-link>
          </li>
          <li
            class="hide-on-mobile"
            v-else-if="isInventoryEnabled && hasConductStocktake && isLoggedIn"
          >
            <router-link :to="{ name: 'venue-stock-stocktake' }">Inventory</router-link>
          </li>
          <li v-if="!$permission.isFree && isLoggedIn && (isInventoryEnabled || isRecipesEnabled)" class="hide-on-mobile">
            <router-link :to="{ name: 'venue-recipes' }">
              {{ isRecipesEnabled ? 'Recipes' : 'Ingredients' }}
            </router-link>
          </li>
          <li
            class="hide-on-mobile"
            v-if="!$permission.isFree && $permission.has('seeReports') && isLoggedIn"
          >
            <div class="sub-menu">
              <router-link :to="{ name: 'venue-reports' }">Reports</router-link>
            </div>
            <ul class="sub-menu__dropdown">
              <li>
                <router-link :to="{ name: 'venue-reports-invoices' }">Invoice Reports </router-link>
              </li>
              <li>
                <router-link :to="{ name: 'venue-reports-orders' }">Order Reports </router-link>
              </li>
              <li>
                <router-link :to="{ name: 'venue-reports-menu-margins' }">Menu Margins</router-link>
              </li>
              <li>
                <router-link :to="{ name: 'venue-reports-sales' }">Sales</router-link>
              </li>
              <li v-if="context.chartbrewUrl">
                <router-link :to="{ name: 'venue-reports-chartbrew' }">
                  Chartbrew Reports
                </router-link>
              </li>
            </ul>
          </li>
        </ul>
      </template>
      <template #cart>
        <button
          :data-cy="outletCy.MAIN_NAVIGATION.BUTTON__CART"
          v-if="$permission.has('createOrder')"
          class="cart"
          @click="openCart"
        >
          <font-awesome-icon icon="shopping-cart" />
          <ez-badge type="blue" :count="itemsCount" />
        </button>
      </template>
      <template #accountMenu>
        <account-menu v-if="isLoggedIn" />
        <ez-button v-else @click="goToLogIn" class="ml-8">Sign In</ez-button>
      </template>
      <router-view :key="contextId"></router-view>
    </tab-layout>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import AccountMenu from '@/views/platform/venue/AccountMenu.vue';
import TabLayout from '@/components/layout/Tab.vue';
import EzBadge from '@/components/ui/Badge';
import EzAlert from '@/components/ui/Alert/EzAlert.vue';
import EzButton from '@/components/ui/Button';
import { resetState } from '@/store';
import { outlet as outletCy } from '@orderez/qa-utils/dist/orderEz/outlet';
import { ACCOUNT_STATUS_TRIAL_EXPIRED, LOADING_KEY } from '@/util/constants';
import { COMMON as commonCy } from '@orderez/qa-utils/dist/orderEz/common';
import { isB2BPlatform } from '@/util/utils';
import flash from '@/components/ui/FlashMessage';

export default {
  components: {
    AccountMenu,
    TabLayout,
    EzBadge,
    EzAlert,
    EzButton,
  },
  data() {
    return {
      outletCy,
      commonCy,
      imageData: null,
      LOADING_KEY,
      hasInvoice: false,
      fileType: '',
      token: null,
      isB2BPlatform,
    };
  },
  computed: {
    isLoggedIn() {
      return Object.keys(this.loggedUser).length > 0;
    },
    pendingCount() {
      return this.me.pendingCount || 0;
    },
    ...mapGetters('loading', ['getLoading']),
    oezPlatformName() {
      return process.env.VUE_APP_PLATFORM_TITLE;
    },
    isUploadLoading() {
      return this.getLoading(LOADING_KEY.LOG_OFFLINE_ORDER);
    },
    formAction() {
      return `venues/${this.contextId}/pending-invoices`;
    },
    ...mapGetters({
      itemsCount: 'cart/getItemCount',
    }),
    ...mapState('entities/users', ['context', 'contextId', 'loggedUser']),
    groupViewMode() {
      if (this.loggedUser.context === 'venue-group') return true;
      return false;
    },
    isInventoryEnabled() {
      return this.context?.features?.inventory || false;
    },
    isRecipesEnabled() {
      return this.context?.features?.recipeManagement || false;
    },
    canSeeConnectionRequest() {
      return this.context?.invitationsCount > 0 && !this.$permission.isFree;
    },
    hasManageInventory() {
      return this.$permission.has('manageInventory');
    },
    hasConductStocktake() {
      return this.$permission.has('conductStocktake');
    },
    hasManageInventoryAndConductStocktake() {
      return this.$permission.has('manageInventory') && this.$permission.has('conductStocktake');
    },
    banner() {
      return this.context?.banner || null;
    },
    isTrialExpired() {
      return this.context?.subscriptionStatus === ACCOUNT_STATUS_TRIAL_EXPIRED;
    },
    isTrial() {
      return this.context?.isTrial ?? false;
    },
    isFreemium() {
      return this.context?.accountType === 'free' ?? false;
    },
    css() {
      return `:root {
        --banner-primary-color: ${this.banner.primaryColor};
        --banner-secondary-color: ${this.banner.secondaryColor};
      }`;
    },
    supplierNotificationNumber() {
      return this.context?.invitationsCount || 0;
    },
    pendingInvoicesCount() {
      if (!this.groupViewMode) {
        return this.context.pendingInvoicesCount;
      }
      return this.loggedUser.venues.reduce((sum, v) => sum + v.pendingInvoicesCount || 0, 0);
    },
  },
  methods: {
    openCart() {
      this.$router.push({
        name: 'venue-shopping-cart',
        ...(this.token
          ? {
              query: {
                token: this.token,
              },
            }
          : {}),
      });
    },
    goToLogIn() {
      this.$router.push({
        name: 'platform-login',
      });
    },
    ...mapActions('entities/users', ['initVenue', 'managerLogout']),
    onBannerCTAClick() {
      if (this.banner.path.startsWith('https')) {
        window.open(this.banner.path, '_blank');
      } else {
        this.$router.push(this.banner.path);
      }
    },
    onLogoutClick() {
      this.managerLogout().finally(() => {
        const logInMethod = localStorage.getItem('logInMethod');
        localStorage.clear();
        localStorage.setItem('logInMethod', logInMethod);
        resetState();
        window.Intercom('shutdown');
        this.$router.replace({ name: 'platform-login' });
      });
    },
    async imagesUploaded(formData) {
      this.hasInvoice = formData.get('invoices');
      this.imageData = formData;
      await this.$nextTick();
      this.$refs.uploadInvoiceForm.onSubmit();
    },
    confirmSuccess() {
      this.reset();
      flash.success({
        title: 'Invoice successfully uploaded',
      });
    },
  },
  created() {
    if (this.isLoggedIn) this.initVenue();
    else if (this.$route?.query.token) {
      const { token } = this.$route?.query;
      this.token = token;
    }
  },
};
</script>

<style scoped lang="scss">
$tab-text-color: #8790a3;
$active-tab-border-color: #4d7cfe;
$item-count-color: #ffffff;

:deep() div.alert.alert--banner.venue-banner {
  margin-top: 0;
  margin-bottom: 0;
  background-color: var(--banner-secondary-color);
  color: var(--banner-primary-color);

  &__message,
  .button,
  svg {
    color: var(--banner-primary-color);
  }

  .button {
    background-color: $color-white;
  }

  .banner__link {
    background: transparent;
    color: var(--banner-primary-color);
    font-weight: 500;
    text-decoration: none;
  }
  .sign-out {
    position: absolute;
    right: 0;
  }
}

.layout-tab__navigation {
  .cart {
    @extend %button-reset;
    @extend %flex-center;
    margin-right: 0.375rem;
    outline: 0;
    background: none;
    cursor: pointer;

    :deep() .badge {
      flex-shrink: 0;
      margin-right: 0;
    }
  }

  .sub-menu {
    display: flex;
    align-items: center;
    height: 100%;
    a {
      height: 100%;
    }
    &__dropdown {
      display: none;
      flex-direction: column;
      position: absolute;
      top: 54px;
      margin: 0;
      margin-left: -16px;
      padding: 8px 16px;
      z-index: 100;
      background-color: #fff;
      border-radius: 3px;
      border: 1px solid #eceef5;
      border-top: none;
      height: fit-content;
      width: max-content;

      &:hover {
        display: flex;
      }

      li {
        margin-left: 0;
        a {
          padding: 8px 0;
        }
      }
    }
    &:hover + .sub-menu__dropdown {
      display: flex;
    }
  }
}

.mobile-screen {
  display: none;
}

:deep() .layout-tab.b2b {
  min-width: auto;
}

@media (max-width: 768px) {
  .layout-tab__navigation ul li.hide-on-mobile {
    display: none;
  }

  .mobile-screen {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 16px;
    text-align: center;

    hr {
      width: 100%;
    }

    &__logo {
      height: 40px;
    }
    .logo-wrapper {
      display: flex;
      align-items: center;
      height: 100%;
    }

    &__title {
      font-size: 20px;
      line-height: 28px;
      font-weight: 600;
      letter-spacing: 0.15px;
      span {
        color: $color-primary-blue;
      }
    }

    &__subtitle {
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 16px;
      line-height: 1.5;
      font-weight: 500;
    }

    &__desc {
      font-size: 12px;
      line-height: 1.5;
      color: $color-gray-6C;
    }

    &__download-apps {
      display: flex;
      align-items: center;
      justify-content: center;
      img {
        height: 32px;
      }
    }

    :deep() .ez-form {
      width: 100%;
    }

    .upload-component {
      :deep() .preview__button label {
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: $color-primary-blue;
        color: white;
        font-size: 16px;
        line-height: 1.5;
        padding: 12px 16px;
        font-weight: 500;
        height: auto;
        svg {
          display: none;
        }
      }

      :deep() .preview__images {
        flex-direction: column;
        justify-content: center;
        align-items: flex-end;
      }
    }
    &__premium {
      padding: 0;
      letter-spacing: 0.15px;

      .mobile-header {
        padding: 16px 24px;
        border-bottom: 1px solid #eceef5;

        display: flex;
        align-items: center;
        justify-content: space-between;

        :deep() .button-dropdown__dropdown {
          max-height: 250px;
          text-align: left;
        }

        :deep() .button-dropdown {
          .button-dropdown__toggle-container {
            .button {
              padding: 0;
            }
            .trigger {
              display: none;
            }
            .entity-info__text {
              display: none;
            }
          }
        }
      }
      .content {
        padding: 32px 24px;

        .upload-component {
          :deep() .preview__button label {
            display: flex;
            margin-bottom: 16px;
            color: $color-gray-6C;
            background-color: #f5f6fa;
            align-items: center;
            justify-content: center;
            font-size: 14px;
            line-height: 1.5;
            padding: 12px 16px;
            font-weight: 500;
            height: auto;
            svg {
              display: block;
            }
          }
        }

        .heading {
          font-size: 20px;
          line-height: 1.4;
          font-weight: 600;
        }

        .subheading {
          font-size: 14px;
          line-height: 1.4;
          font-weight: 400;
          color: $color-gray-6C;
        }
      }
    }
  }
  :deep() .layout-tab.desktop-screen {
    display: none;
  }
}
</style>
